import { Injectable } from "@angular/core";
import { Comentario } from "../../models/Comentario";
import { firstValueFrom, Observable, Subject, tap } from "rxjs";

import { LoggingService } from "../logging.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { ErrorHandlerService } from "../errorHandler.service";
import { AppConfig } from "src/app/app.config";
import { EnumTipoObjeto } from "src/app/enums/EnumTipoObjeto";
import { OperacaoSignalR } from "../../models/OperacaoSignalR";
import { SignalrService } from "../signalr.service";
import { StorageService } from "../storage.service";
import { UsuarioPreferenciasService } from "./usuario.preferencias.service";
import { DataControleRepositorio } from "src/app/repositorios/dataControle.repositorio";
import { EnumTipoDataControle } from "src/app/enums/EnumDataControle";

@Injectable()
export class UsuarioComentariosService {
  readonly databaseName = "userdata";
  readonly collectionName = "comentarios";

  private modificadoComentario: Comentario;
  private modificadoComentario$ = new Subject<Comentario>();

  constructor(
    private signalrService: SignalrService,
    private loggingService: LoggingService,
    private httpClient: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private usuarioPreferenciasService: UsuarioPreferenciasService,
    private storageService: StorageService,
    private dataControleRepositorio: DataControleRepositorio
  ) {
    signalrService
      .getMensagem()
      .subscribe((msg) => this.onMessageReceived(msg));
  }

  public async carregarComentariosPorLei(leiId: string): Promise<Comentario[]> {
    const url = `${AppConfig.apiEndpoint}/Comentarios/Comentarios`;
    let requestParams = new HttpParams();
    let comentarios: Comentario[];

    const comentariolocal: Comentario[] =
      await this.storageService.list<Comentario>(
        this.databaseName,
        this.collectionName,
        (c) => c.idLei === leiId
      );

    if (comentariolocal.length > 0) {
      const cache = await this.VerificaCacheComentarios();

      if (cache) {
        return comentariolocal;
      }
    }

    requestParams = requestParams.append("IdLei", leiId);
    requestParams = requestParams.append(
      "userId",
      this.usuarioPreferenciasService.getConfiguracoesvalue.id
    );

    await firstValueFrom(
      this.httpClient.get<Comentario[]>(url, { params: requestParams }).pipe(
        tap({
          next: (result: Comentario[]) => {
            if (result) {
              this.fromNuvem(result).catch((error) => {
                this.errorHandlerService.handleError(error);
              });
              comentarios = result;
              this.dataControleRepositorio
                .salvar(
                  this.dataControleRepositorio.generateDataControleTipo(
                    EnumTipoDataControle.DataVerificacaoCacheComentarios
                  )
                )
                .then(() => {})
                .catch((error) => this.errorHandlerService.handleError(error));
            } else {
              comentarios = new Array<Comentario>();
            }
          },
          error: (error) => {
            this.errorHandlerService.handleError(error);
            if (comentariolocal) {
              comentarios = comentariolocal;
            } else {
              comentarios = new Array<Comentario>();
            }
          },
        })
      )
    );

    return comentarios;
  }

  public async fromNuvem(comentarios: Comentario[]): Promise<void> {
    return await this.storageService
      .upsertMany(comentarios, this.databaseName, this.collectionName, false)
      .then(() => {})
      .catch((error) => {
        this.errorHandlerService.handleError(error);
      });
  }

  public async atualizar(
    comentario: Comentario,
    sync = true
  ): Promise<Comentario> {
    if (sync) {
      const mensagem = new OperacaoSignalR();
      mensagem.dados = comentario;
      this.signalrService.enviarMensagem(mensagem, EnumTipoObjeto.Comentarios);
    }

    return await this.storageService
      .upsert(comentario, this.databaseName, this.collectionName)
      .then((resultado) => {
        const novo = !resultado.dataHoraModificacao;

        this.modificadoComentario = resultado;

        this.updateModificadoComentario();

        if (novo) {
          this.loggingService.logEvent(
            resultado.mnemonico
              ? "Leitor - Comentar (Título)"
              : "Leitor - Comentar",
            resultado.idLei,
            resultado.id
          );
        }
        return resultado;
      })
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Comentario();
      });
  }

  public remover(comentario: Comentario): Promise<Comentario> {
    comentario.removido = true;
    return this.atualizar(comentario);
  }

  public async buscarLei(idLei: string): Promise<Comentario[]> {
    return await this.storageService
      .list<Comentario>(
        this.databaseName,
        this.collectionName,
        (m) => m.idLei === idLei
      )
      .then((comentarios) => comentarios)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Comentario>();
      });
  }

  public async buscarLinhaVersao(
    idLinha: string,
    versaoLinha: number
  ): Promise<Comentario[]> {
    return await this.storageService
      .list<Comentario>(this.databaseName, this.collectionName, (c) => {
        c.range.idItens.forEach((id) => {
          if (id.idItem === idLinha && id.idImportacao === versaoLinha) {
            return true;
          }
        });

        return false;
      })
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Comentario>();
      });
  }

  public async buscarLinha(idLinha: string): Promise<Comentario[]> {
    return await this.storageService
      .list<Comentario>(this.databaseName, this.collectionName, (c) => {
        c.range.idItens.forEach((id) => {
          if (id.idItem === idLinha) {
            return true;
          }
        });

        return false;
      })
      .then((marcacoes) => marcacoes)
      .catch((error) => {
        this.errorHandlerService.handleError(error);
        return new Array<Comentario>();
      });
  }

  updateModificadoComentario() {
    this.modificadoComentario$.next(this.modificadoComentario);
  }

  getModificadoComentario(): Observable<Comentario> {
    return this.modificadoComentario$.asObservable();
  }

  private onMessageReceived(msg: OperacaoSignalR): void {
    if (msg && msg.tipoObjeto === EnumTipoObjeto.Comentarios) {
      this.atualizar(msg.dados, false);
    }
  }

  private async VerificaCacheComentarios(): Promise<boolean> {
    const lastDocumentsListCheck = await this.dataControleRepositorio.buscar(
      EnumTipoDataControle.DataVerificacaoCacheComentarios
    );
    const tempoEmMinutos = AppConfig.cacheConteudo;

    if (lastDocumentsListCheck) {
      const cacheConteudo: number = tempoEmMinutos * 60 * 1000;
      const ultimaModificacao: Date =
        lastDocumentsListCheck.dataHoraModificacao;
      const DataAtual: Date = new Date();

      if (DataAtual.getTime() - ultimaModificacao.getTime() < cacheConteudo) {
        return true;
      }
    }
    return false;
  }
}
